import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import UserAvatar from '../components/UserAvatar';
import { PLANBASE_CONFIG } from '../config';
import { getUserAuthHeaders } from '../utils/auth';
import { MobilePageHeader } from '../components/MobilePageHeader';
import { useHideBottomNav } from '../hooks/useHideBottomNav';

import { ArrowPathIcon } from '@heroicons/react/24/outline';

export default function EditProfile() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  useHideBottomNav();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [formData, setFormData] = useState({
    name: user?.name || '',
    title: user?.title || '',
    bio: user?.metadata?.bio || '',
    location: user?.metadata?.location || '',
    website: user?.metadata?.website || '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setMessage(null);
    try {
      const nameParts = formData.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const response = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/members/${user?.id}`, {
        method: 'PUT',
        headers: getUserAuthHeaders(),
        body: JSON.stringify({
          metadata: {
            first_name: firstName, last_name: lastName,
            full_name: formData.name, title: formData.title,
            bio: formData.bio, location: formData.location,
            website: formData.website, avatar_url: user?.avatar_url,
          }
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to update profile');
      }

      const updatedUser = {
        ...user, name: formData.name, title: formData.title,
        metadata: { ...user?.metadata, first_name: firstName, last_name: lastName, full_name: formData.name, title: formData.title, bio: formData.bio, location: formData.location, website: formData.website },
      };
      localStorage.setItem('planbase_user', JSON.stringify(updatedUser));
      refreshUser();
      setMessage({ type: 'success', text: 'Profile updated!' });
      setTimeout(() => navigate('/profile'), 1200);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to update profile' });
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <MobilePageHeader title="Edit Profile" onBack={() => navigate('/profile')} />
      {/* Desktop header */}
      <div className="hidden md:flex items-center justify-between">
        <h1 className="text-sm font-semibold text-foreground uppercase tracking-wide">Edit Profile</h1>
        <Button onClick={() => navigate('/profile')} variant="outline" size="sm">Cancel</Button>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="bg-card border border-border rounded-2xl overflow-hidden">

          {/* Avatar row */}
          <div className="p-5 flex items-center gap-4 border-b border-border">
            <UserAvatar name={user?.name} avatarUrl={user?.avatar_url} size="lg" className="h-14 w-14 text-xl flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">{user?.name}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{user?.email}</p>
            </div>
          </div>

          {/* Fields */}
          <div className="divide-y divide-border">
            <div className="px-5 py-4">
              <label className="text-xs text-muted-foreground block mb-1.5">Full Name *</label>
              <Input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Your full name" required />
            </div>

            <div className="px-5 py-4">
              <label className="text-xs text-muted-foreground block mb-1.5">Professional Title</label>
              <Input type="text" name="title" value={formData.title} onChange={handleChange} placeholder="e.g. Architect, Designer" />
            </div>

            <div className="px-5 py-4">
              <label className="text-xs text-muted-foreground block mb-1.5">Bio</label>
              <textarea
                name="bio" value={formData.bio} onChange={handleChange}
                placeholder="Tell us about yourself..."
                rows={3} maxLength={500}
                className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-primary bg-background text-foreground text-sm resize-y min-h-[80px] max-h-[200px] scrollbar-hide"
              />
              <p className="text-xs text-muted-foreground mt-1">{formData.bio.length}/500</p>
            </div>

            <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground block mb-1.5">Location</label>
                <Input type="text" name="location" value={formData.location} onChange={handleChange} placeholder="City, Country" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1.5">Website</label>
                <Input type="url" name="website" value={formData.website} onChange={handleChange} placeholder="https://yoursite.com" />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 py-4 bg-muted/40 border-t border-border flex items-center justify-between gap-3">
            {message && (
              <p className={`text-xs ${message.type === 'success' ? 'text-green-600' : 'text-destructive'}`}>{message.text}</p>
            )}
            <div className="flex gap-2 ml-auto">
              <Button type="button" variant="outline" size="sm" onClick={() => navigate('/profile')}>Cancel</Button>
              <Button type="submit" size="sm" disabled={loading} className="bg-primary hover:bg-primary/90 text-white">
                {loading ? <><ArrowPathIcon className="mr-1.5 h-3.5 w-3.5 animate-spin" />Saving...</> : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
